import { useEffect, useId, useRef, useState } from "react";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { Box } from "../Box";
import { Button } from "../Button";
import type { IconName } from "../../assets/icons";
import { ConnectorImage } from "../ConnectorImage";
import { Icon } from "../Icon";
import { INFO_ICON_IDLE_OPACITY } from "../InfoIcon";
import { ListTable } from "../ListTable";
import { ReadmeDescriptionTooltip } from "../ReadmeDescriptionTooltip";
import { SelectableMatchIcon } from "../SelectableMatchIcon";
import {
  AnchoredListPopover,
  DetailLabel,
  DetailList,
  DetailRow,
  IconBadge,
  ListBox,
  MatchRow,
  MatchSectionLabel,
  NeutralIconBadge,
  PlainInput,
  RowNameLayout,
  SearchBar,
  SearchIconWrap,
  SearchInputWrap,
} from "../Settings/shared";
import { Text } from "../Text";

type Item = {
  name: string;
  title?: string;
  description?: string;
  /** Shown under the title instead of the name. */
  detail?: string;
  /** Replaces the picker's icon for this item. */
  logo?: string;
};

function ItemIcon({ item, icon }: { item: Item; icon: IconName }) {
  return item.logo ? (
    <ConnectorImage src={item.logo} alt="" style={{ width: 16, height: 16 }} />
  ) : (
    <Icon name={icon} size={16} />
  );
}

function ItemBadge({ item, icon }: { item: Item; icon: IconName }) {
  return item.logo ? (
    <IconBadge
      style={{
        background: "var(--alpha4)",
        width: 32,
        height: 32,
        borderRadius: 8,
      }}
    >
      <ItemIcon item={item} icon={icon} />
    </IconBadge>
  ) : (
    <NeutralIconBadge iconName={icon} />
  );
}

function ItemLabel({ item }: { item: Item }) {
  const secondary =
    item.detail ??
    (item.title && item.title !== item.name ? item.name : undefined);
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {item.title || item.name}
        </Text>
        {item.description && (
          <ReadmeDescriptionTooltip
            description={item.description}
            idleOpacity={INFO_ICON_IDLE_OPACITY}
          />
        )}
      </div>
      {secondary && (
        <Text variant="text-small" color="var(--text-secondary)" ellipsis>
          {secondary}
        </Text>
      )}
    </div>
  );
}

// Without `refresh`, the items are a fixed catalog: no refresh button and no
// entering names outside it.
export function AllowListPicker({
  items,
  noun,
  icon,
  selected,
  disabled,
  error,
  onToggle,
  refresh,
}: {
  items: readonly Item[];
  noun: { singular: string; plural: string };
  icon: IconName;
  selected: ReadonlySet<string>;
  disabled?: boolean;
  error?: string | null;
  onToggle: (name: string) => void;
  refresh?: { fetching: boolean; onRefresh?: () => void };
}) {
  const fetching = refresh?.fetching ?? false;
  const plural = noun.plural;
  const Plural = plural[0]!.toUpperCase() + plural.slice(1);
  const itemByName = new Map(items.map((item) => [item.name, item]));
  for (const name of selected) {
    if (!itemByName.has(name)) itemByName.set(name, { name });
  }
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [searchBarRef, popoverRef] = useMouseDownOutside(() => setOpen(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const matchRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollHighlightRef = useRef(false);
  const listId = useId();
  const needle = search.trim().toLowerCase();
  const matches = [...itemByName.values()].filter((item) =>
    [item.name, item.title].some((value) =>
      value?.toLowerCase().includes(needle)
    )
  );
  const customName = search.trim();
  const canAdd =
    !!refresh && customName.length > 0 && !itemByName.has(customName);
  const options = canAdd ? [...matches, { name: customName }] : matches;
  const highlighted = Math.min(highlightedIndex, options.length - 1);

  useEffect(() => {
    if (scrollHighlightRef.current) {
      matchRefs.current[highlighted]?.scrollIntoView({ block: "nearest" });
      scrollHighlightRef.current = false;
    }
  }, [highlighted]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (
        event.key !== "Escape" ||
        !searchBarRef.current?.contains(event.target as Node)
      )
        return;
      // The modal handles Escape at document capture, so close this picker first.
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
      setSearch("");
      setHighlightedIndex(0);
    };
    window.addEventListener("keydown", closeOnEscape, true);
    return () => window.removeEventListener("keydown", closeOnEscape, true);
  }, [open, searchBarRef]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!disabled && (
        <SearchBar
          ref={searchBarRef}
          onMouseDown={(event) => {
            if ((event.target as HTMLElement).closest("button, input")) return;
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <SearchInputWrap>
            <SearchIconWrap>
              <Icon name="search" size={16} />
            </SearchIconWrap>
            <PlainInput
              ref={inputRef}
              role="combobox"
              aria-label={`Add ${plural}`}
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={open ? listId : undefined}
              aria-activedescendant={
                open && highlighted >= 0
                  ? `${listId}-${highlighted}`
                  : undefined
              }
              placeholder={`Add ${plural}…`}
              value={search}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onChange={(event) => {
                setSearch(event.target.value);
                setHighlightedIndex(0);
                setOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  scrollHighlightRef.current = true;
                  setOpen(true);
                  const next =
                    highlighted + (event.key === "ArrowDown" ? 1 : -1);
                  setHighlightedIndex(
                    open ? Math.max(0, Math.min(next, options.length - 1)) : 0
                  );
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  const option = options[highlighted];
                  if (open && option) onToggle(option.name);
                  else setOpen(true);
                } else if (event.key === "Tab") {
                  setOpen(false);
                }
              }}
            />
          </SearchInputWrap>
          {refresh && (
            <Button
              type="button"
              variant="secondary"
              icon="refresh-cw"
              iconSpinning={fetching}
              disabled={fetching || !refresh.onRefresh}
              aria-label={`Refresh ${plural}`}
              onClick={refresh.onRefresh}
            />
          )}
          <AnchoredListPopover
            anchorRef={searchBarRef}
            popoverRef={popoverRef}
            open={open}
          >
            <div
              id={listId}
              role="listbox"
              aria-label={Plural}
              aria-multiselectable="true"
            >
              <MatchSectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  {fetching ? `Loading ${plural}…` : Plural}
                </Text>
              </MatchSectionLabel>
              {options.map((item, index) => (
                <MatchRow
                  key={item.name}
                  id={`${listId}-${index}`}
                  ref={(element) => {
                    matchRefs.current[index] = element;
                  }}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={selected.has(item.name)}
                  data-highlighted={highlighted === index}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onToggle(item.name);
                  }}
                >
                  <SelectableMatchIcon
                    staged={selected.has(item.name)}
                    preview={false}
                    onToggle={() => onToggle(item.name)}
                  >
                    <ItemIcon item={item} icon={icon} />
                  </SelectableMatchIcon>
                  {canAdd && index === matches.length ? (
                    <Text variant="text-regular">Add “{customName}”</Text>
                  ) : (
                    <ItemLabel item={item} />
                  )}
                </MatchRow>
              ))}
              {options.length === 0 && !fetching && (
                <Box horizontal={12} vertical={12}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {refresh
                      ? `No ${plural} found. Refresh to try again, or enter a ${noun.singular} name.`
                      : `No ${plural} match.`}
                  </Text>
                </Box>
              )}
            </div>
          </AnchoredListPopover>
        </SearchBar>
      )}
      {error && (
        <div role="alert">
          <Text variant="text-small" color="var(--text-danger)">
            {error}
          </Text>
        </div>
      )}
      {selected.size > 0 ? (
        <ListBox data-compact="true" style={{ flex: "none", maxHeight: 320 }}>
          <ListTable
            headers={{
              name: `Allowed ${plural} (${selected.size})`,
              actions: "",
            }}
            columns={{ name: "flex" }}
            items={[...selected].map((name) => ({
              key: name,
              name: (
                <RowNameLayout>
                  <ItemBadge item={itemByName.get(name)!} icon={icon} />
                  <ItemLabel item={itemByName.get(name)!} />
                </RowNameLayout>
              ),
              actions: disabled ? null : (
                <Button
                  type="button"
                  variant="secondary"
                  icon="x"
                  aria-label={`Remove ${name}`}
                  onClick={() => onToggle(name)}
                />
              ),
            }))}
          />
        </ListBox>
      ) : (
        <DetailList>
          <DetailRow>
            <NeutralIconBadge iconName="circle-slash" />
            <DetailLabel>
              <Text variant="text-regular-md">No {plural} allowed</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                {Plural} you allow this connector to use will appear here.
              </Text>
            </DetailLabel>
          </DetailRow>
        </DetailList>
      )}
    </div>
  );
}
